import useGetCampaign from "hooks/useGetCampaign";
import useAuthViewer from "hooks/useAuthViewer";
import React, { useEffect, useMemo, useState } from "react";
import "./style.scss";
import { IconFlag } from "components/Icon";
import ImageCarousel from "components/ImageCarousel";
import CampaignHero from "components/CampaignHero";
import { useQuery } from "@apollo/client";

import { GET_FUNDRAISER } from "queries/hooks";
import LinkButton from "components/LinkButton";
import CampaignStatus from "components/CampaignStatus";

import Perks from "widgets/Perks";
import ProgressMeter from "components/ProgressMeter/ProgressMeter";
import Comments from "widgets/Comments";
import * as Sentry from "@sentry/react";
import MatchedGiving from "components/MatchedGiving";
import OnboardingCompleteMultiStepModal from "components/MultiStepModal/OnboardingComplete";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import ViewMoreContentWrapper from "pages/campaign/components/ViewMoreContentWrapper/ViewMoreContentWrapper";
import CampaignUpdatesWrapper from "pages/campaign/components/CampaignUpdates/CampaignUpdatesWrapper";
import FeaturedByWrapper from "pages/campaign/components/FeaturedBy/FeaturedByWrapper";
import useGetCampaignCountdown from "hooks/useGetCampaignCountdown";
import useSegmentTracking from "hooks/useSegmentTracking";
import TabbedContainer from "components/TabbedContainer";
import ShareModal from "components/ShareModal/ShareModal";
import { filterUrl } from "helpers/filterUrl";
import usePersistTrackingCookies from "hooks/usePersistTrackingCookies";
import CampaignSkeleton from "./components/Skeleton";
import Leaderboard from "./components/Leaderboard";
import CampaignSideBar from "./components/CampaignSideBar";
import CampaignShareBar from "./components/ShareBar";
import InfoBar from "./components/InfoBar";
import {
  getEntity, stripShareSuffix, buildShareUrl, getShareItemForCampaign,
} from "./helpers";
import CampaignContent from "./components/CampaignContent";
import Supporters from "./components/Supporters";
import TeamListing from "./components/TeamListing";
import useGetShareCount from "./hooks/useGetShareCount";
import useGetSupporters from "./hooks/useGetSupporters";

const route = window.location.hash.substring(1);

const CampaignPage = (props) => {
  const {
    campaignId,
    challengerId,
    paymentUrl,
    reportUrl,
    teamsLeaderboard,
    fundraiserSignup,
    fundraisersLeaderboard,
    type,
    fundraiserUserImage,
    fundraiserId,
    isCommentsEnabled,
    initialSection,
    initialUpdateId,
    shouldShareUpdate,
    showFeaturedBy,
    shouldShareCampaign,
  } = props;

  const { loading, campaign } = useGetCampaign(campaignId);
  const { fundraiser, loading: fundraiserLoading } = useGetFundraiser(challengerId, type);
  const entity = useMemo(() => getEntity(type, campaign, fundraiser), [campaign, fundraiser, type]);
  const { viewer: user, loading: loadingAuthViewer } = useAuthViewer();
  const isAdmin = useGetIsAdmin(campaign, user);
  const { translate: t } = useLanguageProvider();
  const { track } = useSegmentTracking();
  usePersistTrackingCookies();
  const shareTrackingSource = challengerId ? "team_page_sharebar" : "campaign_page_sharebar";
  const shareUtmCampaign = challengerId ? "fundraiser" : "campaign";
  const [shareModalOpen, setShareModalOpen] = useState(() => !!shouldShareCampaign);
  const [displayPerksContainer, setDisplayPerks] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(initialSection === "updates" ? 1 : 0);
  const shareCount = useGetShareCount(campaignId);
  const {
    campaignEndsTime, isLessThanOneDay, setEndTime, getBrowserEndTimeString, hasEnded,
  } = useGetCampaignCountdown();
  const {
    loading: loadGetSupporters,
    supporters,
    totalSupporters,
    viewMoreSupporters,
  } = useGetSupporters(campaignId, fundraiserId, type);

  const CommentData = useMemo(
    () => (
      <div
        className="campaign-container__comments-container"
        data-test="campaign-comments-container"
      >
        <Sentry.ErrorBoundary fallback={null}>
          <Comments
            campaignId={campaignId}
            campaignTitle={campaign?.title ?? ""}
            challengerId={challengerId}
            user={user}
            campOwner={campaign?.ownerName}
          />
        </Sentry.ErrorBoundary>
      </div>
    ),
    [campaignId, campaign?.title, challengerId, user, campaign?.ownerName],
  );

  const campaignEndTime = useMemo(() => {
    if (campaign) {
      return campaign.duration.finishAtUtc;
    }
    return null;
  }, [campaign]);

  const commentsContent = useMemo(
    () => <ViewMoreContentWrapper content={CommentData} expandText="View all comments" />,
    [CommentData],
  );

  useEffect(() => {
    if (campaignEndTime) {
      setEndTime(campaignEndTime);
    }
  }, [campaignEndTime, setEndTime]);

  const contentLoading = loading || (type === "fundraiser" && fundraiserLoading) || loadingAuthViewer;

  if (!campaign || contentLoading) {
    return <CampaignSkeleton />;
  }

  const images = type === "campaign" ? campaign.assets : (fundraiser?.assets ?? []);
  const shouldShowStatus = campaign.status && campaign.status !== "Live" && !campaign.duration.isInfinityMode && isAdmin;
  const hasMultipleImages = images && images.length >= 2;
  const campaignIsActive = campaign.status === "Live" || campaign.status === "Approved" || campaign.status === "Infinity";
  const matchedGivingLive = campaign.matchedGivings.length > 0 && campaign.matchedGivings[0].id;
  const shouldShowMatchedGiving = campaign.matchedGivings.length > 0 && !campaign.matchedGivings[0].hasMetTarget;
  const shouldAllowDonations = campaignIsActive && !(hasEnded && !campaign.duration.isInfinityMode);

  const campaignStartAt = campaign?.duration?.startAt ? new Date(campaign?.duration?.startAt) : null;
  const campaignHasStartAt = campaignStartAt instanceof Date && !isNaN(campaignStartAt);

  const campaignStartDate = campaignHasStartAt
    ? new Intl.DateTimeFormat(undefined, {
      dateStyle: "long",
    }).format(new Date(campaignStartAt))
    : null;

  const toggleShareModal = () => setShareModalOpen((isOpen) => !isOpen);

  const closeShareModal = () => {
    setShareModalOpen(false);
    stripShareSuffix();
  };

  const shareUrl = type === "fundraiser" ? (fundraiser?.url ?? campaign.url) : campaign.url;

  const shareButton = (value) => (
    <LinkButton
      className="campaign-container__share-button"
      onClick={toggleShareModal}
      look="secondary-outline"
      isBold
      size="normal"
      data-test="campaign-share-button"
    >
      {value}
    </LinkButton>
  );

  const reportButton = (value) => (
    <LinkButton
      className="campaign-container__report-button"
      href={reportUrl}
      look="icon-link"
      isIcon
      isBold
      size="normal"
      data-test="campaign-report-button"
    >
      <IconFlag height="2rem" className="campaign-report-icon" />
      {" "}
      {value}
    </LinkButton>
  );

  const handleDisplayPerks = (display) => {
    setDisplayPerks(display);
  };

  const showHero = !!((images && images.length <= 1) || (entity.video && images.length <= 1));

  return (
    <section>
      <div>
        {shouldShowStatus ? (
          <div className="campaign-container__status-container --mobile">
            <CampaignStatus
              isAdmin={isAdmin}
              campaignId={campaignId}
              campaignStatus={campaign.status}
              finishDate={campaign.duration.finishAt}
              finishTimezone={campaign.duration.timezone}
            />
          </div>
        ) : null}

        {hasMultipleImages && <ImageCarousel images={images} video={entity.video} />}

        <div className="campaign-container">
          <div className="campaign-container__content">
            {showHero && (
              <CampaignHero
                imageAltText="Banner image for the campaign"
                imageHref={images[0]?.publicUrl}
                videoHref={entity.video}
                data-test="campaign-hero"
              />
            )}
            <section
              className="campaign-container__information"
              data-testid="campaign-matched-giving"
            >
              {matchedGivingLive && (
                <div className="campaign-container__matched-giving">
                  <MatchedGiving campaignId={campaignId} />
                </div>
              )}
              <h1
                className={`campaign-container__title --page-title ${
                  hasMultipleImages && "--marginTopNone"
                }`}
                data-test="campaign-title"
              >
                {entity.title}
              </h1>
              <div
                className="campaign-container__sticky-donate --mobile"
                data-test="campaign-sticky-bar-mobile"
              >
                <div className="campaign-container__animated-total">
                  <div className="campaign-container__progress-bar">
                    {!entity.collectedTargetProgressPercentHidden && (
                      <ProgressMeter
                        currency={campaign.target.currency}
                        currencySymbol={campaign.target.currencyNode.symbol}
                        raised={entity.raised || 0}
                        target={+entity.target}
                        hideTotal={entity.collectedTargetHidden}
                        fixedPercentage={
                          entity.collectedTargetHidden && entity.collectedTargetProgressPercent
                        }
                      />
                    )}
                    {campaign.duration.finishAt && campaign.duration.timezone && !hasEnded && (
                      <div className="campaign-container__countdown">
                        <span className={isLessThanOneDay ? "timer --closing-soon" : "timer"}>
                          {t("Ends in")}
                          {" "}
                          {campaignEndsTime}
                        </span>
                        {" "}
                        on
                        {" "}
                        {getBrowserEndTimeString(campaign.duration)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <InfoBar
                type={type}
                fundraiserUserImage={fundraiserUserImage}
                fundraiserTeamName={entity.teamName}
                fundraiserTeamSlug={entity.teamSlug}
                campaignId={campaignId}
              />

              <CampaignShareBar
                campaign={campaign}
                totalSupporters={totalSupporters}
                shareCount={shareCount}
                shareButton={shareButton(t("Share"))}
              />

              {showFeaturedBy && (
                <FeaturedByWrapper campaign={campaign} paymentUrl={paymentUrl} />
              )}

              <TabbedContainer activeIndex={activeTabIndex} onTabChange={setActiveTabIndex}>
                <TabbedContainer.Tab label="Story" isDefault>
                  <CampaignContent campaignId={campaignId} fundraiserId={fundraiserId} type={type} />

                  {(campaign?.updates && campaign?.updates?.totalCount > 0) && (
                    <CampaignUpdatesWrapper
                      campaign={campaign}
                      onViewAllUpdates={() => setActiveTabIndex(1)}
                      isPreview
                      isCommentsEnabled={isCommentsEnabled}
                    />
                  )}
                </TabbedContainer.Tab>
                <TabbedContainer.Tab
                  label="Updates"
                  count={campaign?.updates?.totalCount ?? 0}
                  isDisabled={!campaign?.updates || campaign?.updates?.totalCount === 0}
                >
                  <CampaignUpdatesWrapper
                    campaign={campaign}
                    openUpdateId={initialUpdateId}
                    isCommentsEnabled={isCommentsEnabled}
                    showShareOnOpen={shouldShareUpdate}
                  />
                </TabbedContainer.Tab>
              </TabbedContainer>

              {campaign.donationForm.mode === "Perk" && (
                <div
                  className={`campaign-container__perk-container ${
                    displayPerksContainer ? "--display" : ""
                  }`}
                >
                  <Perks
                    campaign={campaign}
                    hasPerks={handleDisplayPerks}
                    fundraiserId={type === "fundraiser" && challengerId ? challengerId : null}
                  />
                </div>
              )}

              <Supporters
                type={type}
                totalSupporters={totalSupporters}
                owner={campaign.ownerName}
                supporters={supporters}
                viewMoreSupporters={viewMoreSupporters}
                isLoading={loadGetSupporters}
                campaignCurrency={campaign.target.currency}
              />

              {campaign && campaign.title && isCommentsEnabled && commentsContent}

              <Leaderboard
                type={type}
                fundraiserSignup={fundraiserSignup}
                teamsLeaderboard={teamsLeaderboard}
                fundraisersLeaderboard={fundraisersLeaderboard}
                campaign={campaign}
              />

              <ShareModal
                isOpen={shareModalOpen}
                onClose={closeShareModal}
                title={t("Share this campaign")}
                campaignId={campaignId}
                challengerId={challengerId}
                trackingSource={shareTrackingSource}
                onShareClick={(platform) => track("campaign_shared", null, {
                  campaignId,
                  filteredUrl: filterUrl(document.URL),
                  medium: platform,
                  source: shareTrackingSource,
                })}
                shareItem={getShareItemForCampaign(campaign, buildShareUrl(shareUrl), shareUtmCampaign)}
              />

              <OnboardingCompleteMultiStepModal
                campaignId={campaignId}
                isOpen={route === "prelaunch" && campaign.status === "Approved" && isAdmin}
              />
            </section>
          </div>

          <div className="campaign-container__status-container --desktop">
            {shouldShowStatus ? (
              <CampaignStatus
                isAdmin={isAdmin}
                campaignId={campaignId}
                campaignStatus={campaign.status}
                finishDate={campaign.duration.finishAt}
                finishTimezone={campaign.duration.timezone}
              />
            ) : null}
            <div
              className="campaign-container__sticky-donate --desktop"
              data-test="campaign-sticky-bar"
            >
              <CampaignSideBar
                campaign={campaign}
                entity={entity}
                viewport="desktop"
                paymentUrl={paymentUrl}
                shouldShowMatchedGiving={shouldShowMatchedGiving}
                ownerName={entity.ownerName}
              />
            </div>
          </div>
        </div>

        {type !== "fundraiser" && <TeamListing campaignId={campaignId} />}

        <div className="campaign-container">
          <div className="sub-footer">
            <div className="report-container">{reportButton(t("Report campaign"))}</div>
            {campaignStartDate && (
              <div className="campaign-start-date">
                {t("Campaign created")}
                {" "}
                {campaignStartDate}
              </div>
            )}
          </div>
        </div>

        <div
          className={
            shouldShowMatchedGiving
              ? "campaign-container__sticky-bar-mobile --mobile --doubleSize"
              : "campaign-container__sticky-bar-mobile --mobile"
          }
        >
          {shareButton(t("Share"))}
          <LinkButton
            href={paymentUrl}
            look="primary"
            isBold
            isDisabled={!shouldAllowDonations}
            size="normal"
            data-test="campaign-donate-button"
            className="campaign-container__donate-button"
          >
            {shouldAllowDonations
              ? shouldShowMatchedGiving
                ? t("Double your donation")
                : t("Donate now")
              : t("Donations closed")}
          </LinkButton>
        </div>
      </div>
    </section>
  );
};

export default CampaignPage;

const useGetIsAdmin = (campaign, user) => {
  if (!campaign || !user) {
    return;
  }

  if (user && user.isAdmin) {
    return true;
  }

  const team = campaign.team;
  const status = campaign.status;
  const userId = user.id;
  const campaignStatuses = ["completed", "live"];

  if (status && campaignStatuses.includes(status.toLocaleLowerCase())) {
    return true;
  }

  if (userId) {
    return userId === campaign.owner.id;
  }

  if (team && team.length > 0) {
    const isTeamMember = team.find((i) => i.id === userId);
    return isTeamMember ? isTeamMember.is_editor : false;
  }

  return false;
};

const useGetFundraiser = (challengerId, type) => {
  const skip = type !== "fundraiser";

  const [fundraiserData, setFundraiserData] = useState(null);
  const { loading, error } = useQuery(GET_FUNDRAISER, {
    variables: {
      id: challengerId,
    },
    skip,
    onCompleted(data) {
      if (data.fundraiser) {
        setFundraiserData(data.fundraiser);
      }
    },
  });
  return {
    fundraiser: fundraiserData,
    loading,
    error,
  };
};
